import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import AdminLayout from "../layouts/adminLayout/AdminLayout";
import {
  createWard,
  deleteWard,
  getCityCorporations,
  getDivisions,
  getThanas,
  getWards,
  normalizeList,
  updateWard,
} from "../config/api/locationApi";

const emptyForm = {
  division_id: "",
  city_corporation_id: "",
  thana_id: "",
  ward_number: "",
};

export default function WardList() {
  const [rows, setRows] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [cityCorporations, setCityCorporations] = useState([]);
  const [thanas, setThanas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filterDivisionId, setFilterDivisionId] = useState("");
  const [filterCityCorporationId, setFilterCityCorporationId] = useState("");
  const [filterThanaId, setFilterThanaId] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const filterCityCorporations = useMemo(() => {
    if (!filterDivisionId) return cityCorporations;
    return cityCorporations.filter(
      (item) => String(item.division_id) === String(filterDivisionId),
    );
  }, [cityCorporations, filterDivisionId]);

  const filterThanas = useMemo(() => {
    if (!filterCityCorporationId) return thanas;
    return thanas.filter(
      (item) =>
        String(item.city_corporation_id) === String(filterCityCorporationId),
    );
  }, [filterCityCorporationId, thanas]);

  const formCityCorporations = useMemo(() => {
    if (!formData.division_id) return [];
    return cityCorporations.filter(
      (item) => String(item.division_id) === String(formData.division_id),
    );
  }, [cityCorporations, formData.division_id]);

  const formThanas = useMemo(() => {
    if (!formData.city_corporation_id) return [];
    return thanas.filter(
      (item) =>
        String(item.city_corporation_id) ===
        String(formData.city_corporation_id),
    );
  }, [formData.city_corporation_id, thanas]);

  const fetchOptions = useCallback(async () => {
    try {
      const [divisionRes, cityCorporationRes, thanaRes] = await Promise.all([
        getDivisions(),
        getCityCorporations(),
        getThanas(),
      ]);
      setDivisions(normalizeList(divisionRes));
      setCityCorporations(normalizeList(cityCorporationRes));
      setThanas(normalizeList(thanaRes));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load location options");
    }
  }, []);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getWards({
        thana_id: filterThanaId || undefined,
        ward_number: appliedSearch || undefined,
      });
      setRows(normalizeList(res));
    } catch (error) {
      console.error(error);
      toast.error("Failed to load wards");
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, filterThanaId]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleSearch = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedSearch(search);
  };

  const handleFilterDivisionChange = (value) => {
    setFilterDivisionId(value);
    setFilterCityCorporationId("");
    setFilterThanaId("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterCityCorporationChange = (value) => {
    setFilterCityCorporationId(value);
    setFilterThanaId("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleOpenCreate = () => {
    setEditId(null);
    setFormData(emptyForm);
    setOpenModal(true);
  };

  const handleOpenEdit = (row) => {
    const thana = thanas.find(
      (item) => String(item.id) === String(row.thana_id),
    );
    const cityCorporation = cityCorporations.find(
      (item) => String(item.id) === String(thana?.city_corporation_id),
    );

    setEditId(row.id);
    setFormData({
      division_id: cityCorporation?.division_id || "",
      city_corporation_id: thana?.city_corporation_id || "",
      thana_id: row.thana_id || "",
      ward_number: row.ward_number || "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditId(null);
    setFormData(emptyForm);
  };

  const handleFormDivisionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      division_id: value,
      city_corporation_id: "",
      thana_id: "",
    }));
  };

  const handleFormCityCorporationChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      city_corporation_id: value,
      thana_id: "",
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.thana_id || !formData.ward_number.trim()) {
        toast.error("Thana and ward number are required");
        return;
      }

      const payload = {
        thana_id: formData.thana_id,
        ward_number: formData.ward_number,
      };

      if (editId) {
        await updateWard({ id: editId, ...payload });
        toast.success("Ward updated successfully");
      } else {
        await createWard(payload);
        toast.success("Ward created successfully");
      }

      handleCloseModal();
      fetchRows();
    } catch (error) {
      console.error(error);
      toast.error("Failed to save ward");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete?")) return;

    try {
      await deleteWard(id);
      toast.success("Ward deleted successfully");
      fetchRows();
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete ward");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "ward_number", headerName: "Ward Number", width: 180 },
    {
      field: "thana_id",
      headerName: "Thana",
      width: 200,
      renderCell: (params) =>
        thanas.find((item) => String(item.id) === String(params.value))?.name ||
        params.value ||
        "-",
    },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box className="flex gap-2">
          <Button
            size="small"
            variant="contained"
            onClick={() => handleOpenEdit(params.row)}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout title="Ward">
      <Box className="p-6 bg-white !rounded">
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-6">
          <TextField
            label="Ward Number"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Select
            size="small"
            displayEmpty
            value={filterDivisionId}
            onChange={(e) => handleFilterDivisionChange(e.target.value)}
          >
            <MenuItem value="">All Divisions</MenuItem>
            {divisions.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            displayEmpty
            value={filterCityCorporationId}
            onChange={(e) => handleFilterCityCorporationChange(e.target.value)}
            disabled={!filterDivisionId}
          >
            <MenuItem value="">All City Corporations</MenuItem>
            {filterCityCorporations.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            displayEmpty
            value={filterThanaId}
            onChange={(e) => {
              setFilterThanaId(e.target.value);
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            disabled={!filterCityCorporationId}
          >
            <MenuItem value="">All Thanas</MenuItem>
            {filterThanas.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </Select>

          <Button variant="contained" onClick={handleSearch}>
            Search
          </Button>
          <Button variant="outlined" onClick={handleOpenCreate}>
            Add Ward
          </Button>
        </div>

        <div style={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
          />
        </div>

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{editId ? "Edit Ward" : "Add Ward"}</DialogTitle>
          <DialogContent>
            <Box className="grid grid-cols-1 gap-4 mt-3">
              <Select
                size="small"
                displayEmpty
                value={formData.division_id}
                onChange={(e) => handleFormDivisionChange(e.target.value)}
              >
                <MenuItem value="">Select Division</MenuItem>
                {divisions.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                displayEmpty
                value={formData.city_corporation_id}
                onChange={(e) =>
                  handleFormCityCorporationChange(e.target.value)
                }
                disabled={!formData.division_id}
              >
                <MenuItem value="">Select City Corporation</MenuItem>
                {formCityCorporations.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                displayEmpty
                value={formData.thana_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    thana_id: e.target.value,
                  }))
                }
                disabled={!formData.city_corporation_id}
              >
                <MenuItem value="">Select Thana</MenuItem>
                {formThanas.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>

              <TextField
                label="Ward Number"
                size="small"
                value={formData.ward_number}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    ward_number: e.target.value,
                  }))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
}
